"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, Sparkles, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { MarkdownComponents } from "./markdown-components";
import type { SummaryTypeConfig } from "./types";

interface CompactViewProps {
  Icon: React.ComponentType<{ className?: string }>;
  config: SummaryTypeConfig;
  displayedContent: string;
  isLoading: boolean;
  isStreaming: boolean;
  isTyping: boolean;
  hasError: boolean;
  error?: string;
  summary: any;
  showRefreshButton: boolean;
  onGenerate: () => void;
  onRefresh: () => void;
  className?: string;
}

/**
 * Compact view variant for AI summary
 */
export function CompactView({
  Icon,
  config,
  displayedContent,
  isLoading,
  isStreaming,
  isTyping,
  hasError,
  error,
  summary,
  showRefreshButton,
  onGenerate,
  onRefresh,
  className,
}: CompactViewProps) {
  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm md:text-base font-medium">{config.label}</span>
          {(isLoading || isStreaming) && (
            <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
          )}
        </div>
        {showRefreshButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={summary ? onRefresh : onGenerate}
            disabled={isLoading || isStreaming} title="Refresh"
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
        )}
      </div>

      {hasError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm md:text-base">{error}</AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      )}

      {displayedContent && (
        <div className="text-sm md:text-base text-muted-foreground md:text-foreground">
          <MarkdownComponents markdownContent={displayedContent} />
          {(isStreaming || isTyping) && (
            <span className="animate-pulse ml-1 text-muted-foreground align-baseline inline">|</span>
          )}
        </div>
      )}

      {!summary && !isLoading && !hasError && (
        <Button variant="outline" size="sm" onClick={onGenerate} className="w-full">
          <Sparkles className="h-3 w-3 mr-1" />
          Generate Summary
        </Button>
      )}
    </div>
  );
}
